import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import React, { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import ApplicationDetailsCard from '../../ApplicationDetailsCard';
import InfoIcon from '@mui/icons-material/Info';
import { useDispatch, useSelector } from 'react-redux';
import { canUnderwrite, convertToNumber, getCustomField, getApplicant } from '../../../utils/appUtils';
import TextField from '@mui/material/TextField';
import { IApplication } from '../../../Interfaces/Application/IApplication';
import produce from 'immer';
import { saveApp, updateEditingApp } from '../../../store/application/actions';
import { ApplicationDispatch, SET_SERVER_MESSAGE } from '../../../store/application/types';
import { useBusinessRulesEngine } from '../../../Data/BusinessRulesData';
import { getHelperText, getHelperTextProps } from './OverviewHelpers';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Button, Checkbox } from '@mui/material';
import { IApplicationQuote, IRuleViolation } from '../../../Interfaces/Application/IApplicationQuote';
import ReportViewerDialog from "../../Dialogs/ReportViewerDialog/ReportViewerDialog";
import { Constants, permissions } from '../../../utils/constants';
import { includes } from 'ramda';
import { StatusWorkflowComponent } from './StatusWorkflowComponent';
import { useDialog } from '../../../providers/DialogProvider';
import { useEmployeeRolePermissions } from '../../../Data/EmployeeRoleData';
import { usePostedAppLock } from '../../../hooks/usePostedAppLock';
import ApplicationReportDialog from '../../Dialogs/ApplicationReportDialog';
import { InvalidStipulationsCard } from '../../InvalidStipulationsCard/InvalidStipulationsCard';
import { TabName } from "./OrderEditorTabs";
import { useConfiguration } from "../../../Data/ConfigurationData";
import JDPowersportButton from "../../JDPowersportButton/JDPowersportButton";
import {add, defaultTradeInVehicle, hasValue, PermissionSet, roundNumber} from "@amsanalytics/react-shared-library";
import { NumericTextBox, TextBox } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import ErrorDialog from "../../Dialogs/ErrorDialog";
import { hasExistingCustomerInformation, userHasPermission } from '../../../utils/utils';
import { ExistingCustomerInformation } from './ExistingCustomerInformation';
import { ILoggedInEmployee } from '../../../Interfaces/Admin/ILoggedInEmployee';
import { fetchConfig } from '../../../utils/HttpClient';
import { AppCustomerStipulationDocumentRequirement } from '../../../Interfaces/AppCustomerStipulationDocumentRequirement';
import { IAppCustomer } from '../../../Interfaces/Application/IAppCustomer';
import { AppState } from '../../../store/rootReducer';
import { authService } from '../../..';

interface ATCValues {
  tierPrimary: number;
  overrideTierPrimary: boolean;
  tierCoBuyer: number;
  overrideTierCoBuyer: boolean;
  overrideJdPower: boolean;
  overrideJdPowerRoughTrade: boolean;
  bikePrice: number;
  bikeType: string;
  titleRegFees: number;
  gap: number;
  gapTerm: number;
  serviceContract: number;
  serviceContractTerm: number;
  serviceFee: number;
  tireAndWheel: number;
  tireAndWheelTerm: number;
  dealTermMonths: number;
  cashDown: number;
  financeDiscount: number;
  tradeAllowance: number;
  tradeLienPayoff: number;
  receiveTaxCreditBool: boolean;
  gapError: boolean;
  docFee: number;
  vitPercent: number;
  maxFrontEndAdvance: number;
  freightPrep: number;
  inspectionFee: number;
  availableFrontend: number;
  availableBackend: number;
  maxBackendAdvance: number;
  maxGap: number;
  minCashPaidAtSigning: number;
  totalFees: number;
  salesTax: number;
  monthlyPayment: number;
  maxPayment: number;
  netPaidToDealer: number;
  vit: number;
  apr: number;
  grossCapCost: number;
  residual: number;
  availableTotalFees: number;
  totalDepreciation: number;
  residualPercent: number;
  titleFee: number;
  totalOfPayments: number;
  netTradeValue: number;
  loanType: string;
  paymentError: boolean;
  residualPercentError: boolean;
  cashPaidAtSigningError: boolean;
  securityDeposit: number;
  paymentAtTerm: number;
  adminSellingPrice: number;
  dispositionFee: number,
  acquisitionFee: number;
  adminCashDown: number;
  fbTotalSaleAmount: number;
  financeCharge: number;
  nptdPercentageOfNada: number;
  terms: number[];
  monthlyPaymentSalesTax: number;
  monthlyTotalPaymentWithTax: number;
  overrideFreightPrep: boolean;
  overrideDocFee: boolean;
  overrideInspectionFee: boolean;
  overrideSalesTax: boolean;
  overrideEngineSize: boolean;
  docsDate: string;
  amountFinanced: number;
  previousAppStatusId: string;
  maxPaymentAfterTax: number;
}

export interface OverviewATCProps {
  appCustomerStipDocRequirements: AppCustomerStipulationDocumentRequirement[];
  appCustomers: IAppCustomer[];
  navigate: (tabName: TabName) => void;
  setNestedTabNameFlag: (arg0: string | undefined) => void;
  handleSaveAppFromOverview: (currentApp: IApplication, originalApplicationStatus: string, bypassAppRequirements: boolean) => Promise<void>;
  // classes: Record<"titleText" | "noBorder" | "fontSize" | "contentText", string>;
}

const initializeATCValues = (app: IApplication, appQuote: IApplicationQuote): ATCValues => {
  const minCashPaid = getCustomField(app, 'fundingBasis_minCashPaidAtSigning');
  return {
    tierPrimary: convertToNumber(getCustomField(app, "config_tierPrimary").value),
    overrideTierPrimary: getCustomField(app, "config_overridetierPrimary").value === "true" ? true : false,
    tierCoBuyer: convertToNumber(getCustomField(app, "config_tierCobuyer").value),
    overrideTierCoBuyer: getCustomField(app, "config_overridetierCobuyer").value === "true" ? true : false,
    overrideJdPower: getCustomField(app, "config_overrideJdPower").value === "true",
    overrideJdPowerRoughTrade: getCustomField(app, "config_overrideRoughTrade").value === "true",
    netTradeValue: convertToNumber(getCustomField(app, "Net Trade Value").value),
    loanType: app?.applicationCustomFields.find(x => x.customField.name === "Loan Type")?.value || 'Lease',
    cashPaidAtSigningError: minCashPaid.value !== undefined &&
      Number.parseFloat(minCashPaid.value) > (app?.applicationQuotes?.[0]?.quote?.cashDown || 0),
    // paymentError:
    //   convertToNumber(getCustomField(app, 'fundingBasis_monthlyPayment').value)
    //   > convertToNumber(getCustomField(app, 'fundingBasis_maxPayment').value)
    //   ||
    //   convertToNumber(getCustomField(app, 'fundingBasis_monthlyPayment').value)
    //   < convertToNumber(getCustomField(app, 'fundingBasis_minPayment').value),
    paymentError:
      appQuote.quote.payment !== undefined && appQuote.quote.payment
      > convertToNumber(getCustomField(app, 'fundingBasis_maxPayment').value)
      ||
      appQuote.quote.payment !== undefined && appQuote.quote.payment
      < convertToNumber(getCustomField(app, 'fundingBasis_minPayment').value),
    residualPercentError: getCustomField(app, "residualPercentError").value === "true" ? true : false,
    bikePrice: app.applicationQuotes?.[0]?.quote?.carSalePrice || 0,
    bikeType: getCustomField(app, "config_currentBikeType").value,
    titleRegFees: app.applicationQuotes?.[0]?.quote?.fees || 0,
    gap: convertToNumber(getCustomField(app, "GAP").value),
    gapTerm: convertToNumber(getCustomField(app, "GAP Term").value),
    serviceContract: convertToNumber(getCustomField(app, "Service Contract").value),
    serviceContractTerm: convertToNumber(getCustomField(app, "Service Contract Term").value),
    serviceFee: convertToNumber(getCustomField(app, "Service Fee").value),
    tireAndWheel: convertToNumber(getCustomField(app, "Tire and Wheel").value),
    tireAndWheelTerm: convertToNumber(getCustomField(app, "Tire and Wheel Term").value),

    dealTermMonths: app.applicationQuotes[0].quote.term || 0,
    cashDown: app.applicationQuotes?.[0]?.quote?.cashDown || 0,
    financeDiscount: app.applicationQuotes[0].quote.discount || 0,
    tradeAllowance: app.applicationQuotes?.[0]?.quote?.tradeAllowance || 0,
    tradeLienPayoff: app.applicationQuotes?.[0]?.quote?.tradeLien || 0,
    receiveTaxCreditBool: getCustomField(app, "Receive Tax Credit").value === "true",
    paymentAtTerm: convertToNumber(getCustomField(app, "Payments At Term").value),


    gapError: convertToNumber(getCustomField(app, 'backEnd_maxGap').value) < convertToNumber(getCustomField(app, "GAP").value),
    docFee: convertToNumber(getCustomField(app, "frontEnd_docFee").value),
    vitPercent: convertToNumber(getCustomField(app, 'frontEnd_vitPercent').value),
    maxFrontEndAdvance: convertToNumber(getCustomField(app, 'frontEnd_maxFrontEndAdvance').value),
    minCashPaidAtSigning: convertToNumber(getCustomField(app, 'fundingBasis_minCashPaidAtSigning').value),
    freightPrep: convertToNumber(getCustomField(app, 'frontEnd_freightPrep').value),
    inspectionFee: convertToNumber(getCustomField(app, 'frontEnd_inspectionFee').value),

    availableFrontend: convertToNumber(getCustomField(app, 'frontEnd_available').value),
    availableBackend: convertToNumber(getCustomField(app, 'backEnd_available').value),
    maxBackendAdvance: convertToNumber(getCustomField(app, 'backEnd_maxBackEndAdvance').value),
    maxGap: convertToNumber(getCustomField(app, 'backEnd_maxGap').value),
    maxPayment: convertToNumber(getCustomField(app, 'fundingBasis_maxPayment').value),
    maxPaymentAfterTax: Math.min(add(convertToNumber(getCustomField(app, 'fundingBasis_maxPayment').value),
      convertToNumber(getCustomField(app, 'Lease Payment Tax').value), 500)),

    salesTax: convertToNumber(getCustomField(app, 'frontEnd_salesTax').value),
    monthlyPayment: app.applicationQuotes?.[0]?.quote?.payment || 0,
    netPaidToDealer: convertToNumber(getCustomField(app, 'fundingBasis_netPaidToDealer').value),
    vit: convertToNumber(getCustomField(app, "VIT").value),
    residual: convertToNumber(getCustomField(app, 'fundingBasis_residualValue').value),
    residualPercent: convertToNumber(getCustomField(app, 'Residual Percent').value),
    grossCapCost: convertToNumber(getCustomField(app, 'Gross Cap Cost').value),
    totalDepreciation: convertToNumber(getCustomField(app, 'Total Depreciation').value),

    titleFee: convertToNumber(getCustomField(app, 'Title Fee').value),
    totalOfPayments: convertToNumber(getCustomField(app, 'Total of Payments').value),
    apr: app.applicationQuotes?.[0]?.quote?.apr || 0,
    totalFees: convertToNumber(getCustomField(app, "Total Fees").value),
    availableTotalFees: convertToNumber(getCustomField(app, "Available Total Fees").value),
    securityDeposit: convertToNumber(getCustomField(app, "Security Deposit").value),
    adminSellingPrice: convertToNumber(getCustomField(app, "Admin Selling Price").value),
    adminCashDown: convertToNumber(getCustomField(app, "Admin Cashdown Price").value),
    dispositionFee: convertToNumber(getCustomField(app, "Disposition Fee").value),
    acquisitionFee: convertToNumber(getCustomField(app, "Acquisition Fee").value),
    fbTotalSaleAmount: convertToNumber(getCustomField(app, "fundingBasis_totalSaleAmount").value),
    financeCharge: convertToNumber(getCustomField(app, "Finance Charge").value),
    nptdPercentageOfNada: convertToNumber(getCustomField(app, "nptdPercentageOfNada").value),
    terms: [12, 24, 30, 36, 42, 48, 54, 60, 66, 72, 78].filter(t => t <= ((app as any).maxTerm || 78)), // maxTerm is added to the object in business rule (Max Term for ATC)
    monthlyPaymentSalesTax: convertToNumber(getCustomField(app, "Lease Payment Tax").value),
    monthlyTotalPaymentWithTax: convertToNumber(getCustomField(app, "Lease Payment Total").value),
    overrideFreightPrep: getCustomField(app, "Override Freight Prep").value === "true" ? true : false,
    overrideDocFee: getCustomField(app, "Override Doc Fee").value === "true" ? true : false,
    overrideInspectionFee: getCustomField(app, "Override Inspection Fee").value === "true" ? true : false,
    overrideSalesTax: getCustomField(app, "Override Sales Tax").value === "true" ? true : false,
    docsDate: getCustomField(app, "Docs Dated").value,
    amountFinanced: convertToNumber(getCustomField(app, "Amount Financed").value),
    overrideEngineSize: getCustomField(app, "Override Engine Size").value === "true" ? true : false,
    previousAppStatusId: getCustomField(app, "Previous AppStatusId").value,
  }
}

export const OverviewATC: React.FC<OverviewATCProps> = props => {
  const dispatch = useDispatch<ApplicationDispatch>();
  const businessRulesEngine = useBusinessRulesEngine();
  const { data: configuration } = useConfiguration();
  const applicationState = useSelector((state: AppState) => state.applications);
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const vehicle = currentApp?.vehicle;
  const loggedInUser = useSelector((state: AppState) => state.applications.loggedInUser);
  const [canEditBike, setCanEditBike] = useState(false);
  const appQuote = currentApp?.applicationQuotes[0];
  const [openDialog, closeDialog] = useDialog();
  const [canView, canEdit, canAct, canActInStatus] = useEmployeeRolePermissions();
  const [vinErrorMessage, setVinErrorMessage] = useState<string | undefined>(undefined);
  const [vinIsLocked, setVinIsLocked] = useState<boolean>(vehicle?.vehicleJdPowersport !== undefined);
  const [tradeVinErrorMessage, setTradeVinErrorMessage] = useState<string | undefined>(undefined);
  const [tradeVinIsLocked, setTradeVinIsLocked] = useState<boolean>(currentApp?.tradeInVehicle?.vehicleJdPowersport !== undefined);
  const [trade2VinErrorMessage, setTrade2VinErrorMessage] = useState<string | undefined>(undefined);
  const [trade2VinIsLocked, setTrade2VinIsLocked] = useState<boolean>(currentApp?.tradeInVehicle2?.vehicleJdPowersport !== undefined);
  const loggedInEmployee: ILoggedInEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  let cashPaidAtSigning = currentApp?.applicationQuotes?.[0]?.quote?.cashDown;
  const tiers = [1, 2, 3, 4, 5, 6, 7, 8];
  let cardColor = "#fff";

  const primary = currentApp && getApplicant(currentApp, Constants.roleApplicantId, true);
  const cobuyer1 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant1Id, true);

  const changeBikeSubmit = () => {
    const approvedBikeMake = currentApp?.applicationCustomFields?.find(x => x.customField.name === "Approved Bike Make")?.value;
    const approvedBikePrice = currentApp?.applicationCustomFields?.find(x => x.customField.name === "Approved Bike NADA Amount")?.value;

    const newBikeMake = currentApp?.vehicle?.make;
    const newBikePrice = currentApp?.vehicle?.nadaValue;

    if ((newBikeMake !== approvedBikeMake || (newBikePrice ?? 0) > parseFloat(approvedBikePrice ?? '0')) && currentApp) {
      const app = produce(currentApp, (currentApp) => {
        currentApp.appStatusId = Constants.applicationStatuses.conditionsTentativelySatisfied.toLowerCase();
      });

      dispatch(updateEditingApp(app, businessRulesEngine));
    }

    dispatch(saveApp(businessRulesEngine, false));
  };

  const showMonthlyPaymentTax = () => {
    const state = primary?.customer?.customerAddress?.find(x => x.default === true)?.state?.abbreviation;
    if (state && state === 'NC') {
      // When NC is selected on calculator tax should be calculated as: Monthly Payment * 3% to be
      //    displayed as an additional line below current Monthly Payment along with an additional Total Monthly Payment line.
      return true;
    } else {
      return false;
    }
  };

  const update = (fn: (app: IApplication) => IApplication | void) => {
    const newValue = produce(currentApp, fn);
    if (newValue) {
      dispatch(updateEditingApp(newValue, businessRulesEngine));
    }
  };

  const updateCustomValues = (formattedValue: any, dbFieldName: string) => {
    update((app) => {
      const customField = app?.applicationCustomFields?.find(x => x.customField.name === dbFieldName);
      if (customField) {
        customField.value = (formattedValue || 0).toString();
      }
    })
  };

  // Initializing Custom Value Variables
  const atcValues: Partial<ATCValues> = currentApp && appQuote ? initializeATCValues(currentApp, appQuote) : {};

  // Max amount to finance.
  const maxAmountToFinanceRuleViolation = currentApp?.applicationQuotes?.[0]?.ruleViolations?.find(x => x.displayFieldId === 'maxAmountToFinance');

  const minLoanAmountNotMet = (atcValues?.netPaidToDealer || 0) < 5000 ? true : false;

  const adjustedCashDue = atcValues.fbTotalSaleAmount!
    - atcValues.maxFrontEndAdvance!
    - (atcValues?.netTradeValue || 0)
    - (atcValues?.gap || 0)
    - (atcValues?.serviceContract || 0)
    - (atcValues?.tireAndWheel || 0);

  const showAdjustedCashDown = (atcValues?.minCashPaidAtSigning || 0) < adjustedCashDue ? true : false;

  const displayLtvCashDue = atcValues?.fbTotalSaleAmount !== undefined
    && currentApp?.applicationQuotes?.[0]?.quote?.cashDown !== undefined
    && adjustedCashDue > currentApp.applicationQuotes[0].quote.cashDown
    && showAdjustedCashDown === true;

  const isLease = atcValues?.loanType === "Lease";
  const { activeUnlock } = usePostedAppLock();
  const canEditInStatus = (): boolean => {
    return currentApp?.unlockedBySuperUser || !!activeUnlock || canActInStatus(currentApp?.appStatusId || "", "Modify");
  }

  const canEditBikeInStatus = (): boolean => {
    return currentApp?.unlockedBySuperUser || !!activeUnlock || canActInStatus(currentApp?.appStatusId || "", "Modify") || canEditBike;
  }
  const canClickBikeChangeButton = (): boolean => {
    return true;
  }

  const viewCustomerAppReport = async (applicationId: string, customerId: string, bearerToken: string) => {
    const config: any = await fetchConfig();
    const baseUrl = `${config.api_endpoint}`;
    openDialog({
      children: (
        <ReportViewerDialog
          title={"Credit Application"}
          reportName={"ATCCreditApplication.trdp"}
          baseUrl={baseUrl}
          clientId={configuration?.clientId}
          parameters={[
            {
              name: "ApplicationId",
              value: applicationId,
            },
            {
              name: "CustomerId",
              value: customerId,
            },
          ]}
          closeDialog={closeDialog}
          bearerToken={bearerToken!}
        />
      ),
      fullScreen: true,
    });
  }

  // User has clicked the 'Credit Application' button
  const handleCreditApplicationClick = async () => {
    const bearerToken = await authService.getToken();
    if (currentApp && bearerToken) {
      // If more than one customer exists for this app, ask the user which
      // customer they want to create a credit application for.
      if (currentApp && currentApp?.appCustomers.length > 1) {
        openDialog({
          children: (
            <ApplicationReportDialog
              appCustomers={currentApp.appCustomers}
              accept={async (result: string) => {
                // Results of this dialog is customerID the user wants to create a report for.
                await viewCustomerAppReport(currentApp.id, result, bearerToken);
              }}
              closeDialog={closeDialog}
            />
          ),
          fullScreen: true,
        });
      } else {
        await viewCustomerAppReport(currentApp.id, currentApp.appCustomers[0].customerId, bearerToken);
      }
    }

  }

  const updateVinErrorMessage = (numberOfModels: number | undefined, initialLoad: boolean) => {
    if (numberOfModels === undefined) {
      setVinErrorMessage('The decoding API call was not successful');
    } else if (numberOfModels > 1) {
      setVinErrorMessage(`Multiple Possible Models (${numberOfModels})`);
    } else if (numberOfModels === 0 && !initialLoad) {
      openDialog({
        children: (
          <ErrorDialog
            title={`Vehicle Errors`}
            errors={["No Powersport models found for this VIN"]}
            closeDialog={closeDialog}
          />
        )
      });
      setVinErrorMessage('No Powersport models found for this VIN');
    } else {
      setVinErrorMessage(undefined);
    }
  }

  const updateTradeVinErrorMessage = (numberOfModels: number | undefined, initialLoad: boolean) => {
    if (numberOfModels === undefined) {
      setTradeVinErrorMessage('The decoding API call was not successful');
    } else if (numberOfModels > 1) {
      setTradeVinErrorMessage(`Multiple Possible Models (${numberOfModels})`);
    } else if (numberOfModels === 0 && !initialLoad) {
      openDialog({
        children: (
          <ErrorDialog
            title={`Vehicle Errors`}
            errors={["No Powersport models found for this VIN"]}
            closeDialog={closeDialog}
          />
        )
      });
      setTradeVinErrorMessage('No Powersport models found for this VIN');
    } else {
      setTradeVinErrorMessage(undefined);
    }
  }

  // Runs on first load
  useEffect(() => {
    updateVinErrorMessage(vehicle?.vehicleJdPowersport?.numberOfModelsForVin ?? 0, true);
    updateVinIsLocked(hasValue(currentApp?.vehicle?.vin));

    updateTradeVinErrorMessage(vehicle?.vehicleJdPowersport?.numberOfModelsForVin ?? 0, true);
    //updateTradeVinIsLocked(hasValue(currentApp?.tradeInVehicle?.vin));
  }, []);


  // We only want to run this when going from New to Submitted Status
  useEffect(() => {
    if (atcValues?.previousAppStatusId?.toUpperCase() === Constants.applicationStatuses.new
      && currentApp?.appStatusId.toUpperCase() === Constants.applicationStatuses.underwriting) {

      const result = businessRulesEngine({ action: "Submit" });
      const autoDenyRuleViolations = result?.errors?.filter(e => (e as IRuleViolation)?.type === 'auto-deny-on-submit') ?? [];
      if (autoDenyRuleViolations.length > 0) {
        const updatedApp = produce(currentApp, (appToUpdate) => {
          appToUpdate.appStatusId = Constants.applicationStatuses.denied;
        });
        //updateCustomValues(Constants.applicationStatuses.denied, "Previous AppStatusId");
        dispatch(updateEditingApp(updatedApp, businessRulesEngine));
        dispatch(saveApp(businessRulesEngine, false));
        dispatch({
          type: SET_SERVER_MESSAGE,
          payload: {
            isError: false,
            message: `The application was automatically denied for the following reasons: ${autoDenyRuleViolations.map(rv => (rv as IRuleViolation)?.message).join(', ')}`,
          },
        })
      }
    }
  }, [currentApp]);


  const updateVinIsLocked = (vinIsLocked: boolean) => {
    if (!hasValue(currentApp?.vehicle?.vin)) {
      setVinIsLocked(false);
      vinIsLocked = false;
    }

    setVinIsLocked(vinIsLocked);
    if (!vinIsLocked) {
      update((app) => {
        if (app?.vehicle) {
          app.vehicle.model = '';
          app.vehicle.make = '';
          app.vehicle.year = 0;
          app.vehicle.engineSize = undefined;
          app.vehicle.nadaValue = undefined;
          app.vehicle.mileage = 0;
          app.vehicle.color = '';

          if (app.vehicle.vehicleJdPowersport) {
            app.vehicle.vehicleJdPowersport.selectedValue = 0;
          }
        }
        getCustomField(app, 'config_roughTrade').value = '';
        getCustomField(app, 'config_cleanTrade').value = '';
        getCustomField(app, 'config_retailTrade').value = '';
        getCustomField(app, 'config_currentBikeType').value = '';
        getCustomField(app, 'config_overrideJdPower').value = 'false';
        getCustomField(app, 'config_roughTrade').value = 'false';
      });
    }
  }


  const updateTradeVinIsLocked = (vinIsLocked: boolean) => {

    if (!hasValue(currentApp?.tradeInVehicle?.vin)) {
      setTradeVinIsLocked(false);
      vinIsLocked = false;
    }

    setTradeVinIsLocked(vinIsLocked);
    if (!vinIsLocked) {
      update((app) => {
        if (app?.tradeInVehicle) {
          app.tradeInVehicle.model = '';
          app.tradeInVehicle.make = '';
          app.tradeInVehicle.year = 0;
          app.tradeInVehicle.tradeValue = 0;
          app.tradeInVehicle.mileage = 0;
          app.tradeInVehicle.color = '';

          if (app.tradeInVehicle.vehicleJdPowersport) {
            app.tradeInVehicle.vehicleJdPowersport.selectedValue = 0;
          }
        }
      });
    }
  }

  const updateTrade2VinErrorMessage = (numberOfModels: number | undefined, initialLoad: boolean) => {
    if (numberOfModels === undefined) {
      setTrade2VinErrorMessage('The decoding API call was not successful');
    } else if (numberOfModels > 1) {
      setTrade2VinErrorMessage(`Multiple Possible Models (${numberOfModels})`);
    } else if (numberOfModels === 0 && !initialLoad) {
      openDialog({
        children: (
          <ErrorDialog
            title={`Vehicle Errors`}
            errors={["No Powersport models found for this VIN"]}
            closeDialog={closeDialog}
          />
        )
      });
      setTrade2VinErrorMessage('No Powersport models found for this VIN');
    } else {
      setTrade2VinErrorMessage(undefined);
    }
  }

  const updateTrade2VinIsLocked = (vinIsLocked: boolean) => {
    if (!hasValue(currentApp?.tradeInVehicle2?.vin)) {
      setTrade2VinIsLocked(false);
      vinIsLocked = false;
    }

    setTrade2VinIsLocked(vinIsLocked);
    if (!vinIsLocked) {
      update((app) => {
        if (app?.tradeInVehicle2) {
          app.tradeInVehicle2.model = '';
          app.tradeInVehicle2.make = '';
          app.tradeInVehicle2.year = 0;
          app.tradeInVehicle2.tradeValue = 0;
          app.tradeInVehicle2.mileage = 0;
          app.tradeInVehicle2.color = '';

          if (app.tradeInVehicle2.vehicleJdPowersport) {
            app.tradeInVehicle2.vehicleJdPowersport.selectedValue = 0;
          }
        }
      });
    }
  }

  return (
    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', height: 'calc(100vh - 112px)', overflowY: 'auto', overflowX: 'hidden' }}>
      {
        hasExistingCustomerInformation(currentApp) && userHasPermission(loggedInEmployee, permissions.existingCustomerInformation, PermissionSet.R) === true && (
          <ExistingCustomerInformation appCustomers={currentApp!.appCustomers} />
        )
      }
      <Grid container spacing={1}>
        <Grid item xs={12} lg={4} container spacing={1} direction="column">
          {/* Customer Information - Primary */}
          <Grid item>
            <Card style={{ backgroundColor: cardColor }}>
              <CardHeader
                avatar={<InfoIcon fontSize="small" />}
                title="Customer Information - Primary"
              />
              <Divider variant="fullWidth" />
              <CardContent style={{ pointerEvents: canEditInStatus() ? 'all' : "none" }}>
                {/* Header */}
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <TextField variant="outlined" size="small"
                      label="First Name"
                      value={primary?.customer?.firstName || ''}
                      onChange={(e) => {
                        update((app) => {
                          const primary = getApplicant(app, Constants.roleApplicantId, true);
                          if (primary?.customer) {
                            primary.customer.firstName = e.target.value;
                          }
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField variant="outlined" size="small"
                      label="Middle Name"
                      value={primary?.customer?.middleName || ''}
                      onChange={(e) => {
                        update((app) => {
                          const primary = getApplicant(app, Constants.roleApplicantId, true);
                          if (primary?.customer) {
                            primary.customer.middleName = e.target.value;
                          }
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField variant="outlined" size="small"
                      label="Last Name"
                      value={primary?.customer?.lastName || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (primary?.customer) {
                            primary.customer.lastName = e.target.value;
                          }
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField variant="outlined" size="small" type="number"
                      label="Mthly Income"
                      value={primary?.customer?.customerEmployment[0].income || null}
                      onChange={(e) => {
                        update((app) => {
                          const primary = getApplicant(app, Constants.roleApplicantId, true);
                          const newValue = parseFloat(e.target.value);
                          if (primary?.customer?.customerEmployment?.[0]) {
                            primary.customer.customerEmployment[0].income = newValue;
                          }
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    {canUnderwrite(loggedInUser?.employee) && (
                      <TextField
                        variant="outlined"
                        size="small"
                        label="Credit Score"
                        InputLabelProps={{ shrink: true }}
                        value={primary?.customer?.customerCredit[0]?.bureauScore || null}
                        error={currentApp?.applicationQuotes?.[0]?.ruleViolations?.find(x => x.displayFieldId === 'bureauScore') !== undefined}
                        helperText={getHelperText('bureauScore', appQuote, currentApp)}
                        FormHelperTextProps={getHelperTextProps('bureauScore', appQuote)}
                        onChange={(e) => {
                          update((app) => {
                            const primary = getApplicant(app, Constants.roleApplicantId, true);
                            if (primary) {
                              primary.customer.customerCredit[0].bureauScore = Number.parseInt(e.target.value);
                            }
                          })
                        }}
                        disabled={(includes(currentApp?.appStatusId.toUpperCase(),
                          [
                            Constants.applicationStatuses.conditionallyApproved,
                            Constants.applicationStatuses.denied
                          ])
                        )
                        }
                      />)}
                  </Grid>
                  <Grid item xs={4}>
                    {canUnderwrite(loggedInUser?.employee) && (
                      <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        <TextField variant="outlined" size="small"
                          label="Tier"
                          value={atcValues?.tierPrimary}
                          disabled={atcValues?.overrideTierPrimary !== true
                            || (includes(currentApp?.appStatusId.toUpperCase(),
                              [
                                Constants.applicationStatuses.conditionallyApproved,
                                Constants.applicationStatuses.denied,
                                Constants.applicationStatuses.conditionallyApproved
                              ])
                            )
                          }
                          fullWidth
                          onChange={(e) => updateCustomValues(e.target.value, "config_tierPrimary")}
                          aria-disabled
                          error={currentApp?.applicationQuotes?.[0]?.ruleViolations?.find(x => x.displayFieldId === 'tierField') !== undefined}
                          helperText={getHelperText('tierField', appQuote, currentApp)}
                          FormHelperTextProps={getHelperTextProps('tierField', appQuote)}
                          SelectProps={{ native: true }}
                          select
                        >
                          {tiers.map((t: number, key) => (
                            <option key={key} value={t}>{`Tier ${t}`}</option>
                          ))}
                        </TextField>
                        <FormControlLabel
                          control={<Checkbox size="small" color="primary" />}
                          label="Override"
                          labelPlacement="start"
                          disabled={includes(currentApp?.appStatusId.toUpperCase(),
                            [
                              Constants.applicationStatuses.conditionallyApproved,
                              Constants.applicationStatuses.denied,
                              Constants.applicationStatuses.conditionallyApproved
                            ])
                          }
                          checked={currentApp?.applicationCustomFields?.find(x => x.customField.name === "config_overridetierPrimary")?.value === "true"}
                          onChange={(e, checked) => updateCustomValues(checked.toString(), "config_overridetierPrimary")}
                        />
                      </div>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {/* Customer Information - Cobuyer */}
          {cobuyer1 && (
            <Grid item>
              <Card style={{ backgroundColor: cardColor, pointerEvents: canEditInStatus() ? 'all' : "none" }}>
                <CardHeader
                  avatar={<InfoIcon fontSize="small" />}
                  title="Customer Information - Cobuyer"
                />
                <Divider variant="fullWidth" />
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <TextField variant="outlined" size="small"
                        label="First Name"
                        value={cobuyer1?.customer?.firstName || ''}
                        onChange={(e) => {
                          update((app) => {
                            const primary = getApplicant(app, Constants.roleCoApplicant1Id, true);
                            if (primary?.customer) {
                              primary.customer.firstName = e.target.value;
                            }
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField variant="outlined" size="small"
                        label="Middle Name"
                        value={cobuyer1?.customer?.middleName || ''}
                        onChange={(e) => {
                          update((app) => {
                            const primary = getApplicant(app, Constants.roleCoApplicant1Id, true);
                            if (primary?.customer) {
                              primary.customer.middleName = e.target.value;
                            }
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField variant="outlined" size="small"
                        label="Last Name"
                        value={cobuyer1?.customer?.lastName || ''}
                        onChange={(e) => {
                          update((app) => {
                            const primary = getApplicant(app, Constants.roleCoApplicant1Id, true);
                            if (primary?.customer) {
                              primary.customer.lastName = e.target.value;
                            }
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField variant="outlined" size="small" type="number"
                        label="Mthly Income"
                        value={cobuyer1?.customer?.customerEmployment[0].income || null}
                        onChange={(e) => {
                          update((app) => {
                            const primary = getApplicant(app, Constants.roleCoApplicant1Id, true);
                            const newValue = parseFloat(e.target.value);
                            if (primary?.customer?.customerEmployment?.[0]) {
                              primary.customer.customerEmployment[0].income = newValue;
                            }
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      {canUnderwrite(loggedInUser?.employee) && (
                        <TextField variant="outlined" size="small"
                          label="Credit Score"
                          value={cobuyer1?.customer?.customerCredit[0]?.bureauScore || null}
                          onChange={(e) => {
                            update((app) => {
                              const coBuyer1 = getApplicant(app, Constants.roleCoApplicant1Id, true);
                              if (coBuyer1) {
                                coBuyer1.customer.customerCredit[0].bureauScore = Number.parseInt(e.target.value);
                              }
                            })
                          }}
                          disabled={(includes(currentApp?.appStatusId.toUpperCase(),
                            [
                              Constants.applicationStatuses.conditionallyApproved,
                              Constants.applicationStatuses.denied,
                              Constants.applicationStatuses.conditionallyApproved
                            ])
                          )
                          }
                        />
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      {canUnderwrite(loggedInUser?.employee) && (
                        <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                          <TextField variant="outlined" size="small"
                            label="Tier"
                            value={atcValues?.tierCoBuyer}
                            disabled={atcValues?.overrideTierPrimary !== true
                              || (includes(currentApp?.appStatusId.toUpperCase(),
                                [
                                  Constants.applicationStatuses.conditionallyApproved,
                                  Constants.applicationStatuses.denied,
                                  Constants.applicationStatuses.conditionallyApproved
                                ])
                              )
                            }
                            onChange={(e) => updateCustomValues(e.target.value, "config_tierCobuyer")}
                            SelectProps={{ native: true }}
                            select
                          >
                            {tiers.map((t: number, index: number) => (
                              <option key={index} value={t}>{`Tier ${t}`}</option>
                            ))}
                          </TextField>
                          <FormControlLabel
                            control={<Checkbox size="small" color="primary" />}
                            label="Override"
                            labelPlacement="start"
                            disabled={includes(currentApp?.appStatusId.toUpperCase(),
                              [
                                Constants.applicationStatuses.conditionallyApproved,
                                Constants.applicationStatuses.denied,
                                Constants.applicationStatuses.conditionallyApproved
                              ])
                            }
                            checked={currentApp?.applicationCustomFields?.find(x => x.customField.name === "config_overridetierCobuyer")?.value === "true"}
                            onChange={(e, checked) => updateCustomValues(checked.toString(), "config_overridetierCobuyer")}
                          />
                        </div>
                      )}

                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
          {/* Bike Information */}
          <Grid item>
            <Card style={{ backgroundColor: cardColor, pointerEvents: canEditBikeInStatus() ? 'all' : "none" }}>
              <CardHeader
                avatar={<InfoIcon fontSize="small" />}
                title="Bike Information"
                action={
                  <>
                    <Button
                      onClick={() => {
                        setCanEditBike(true);
                      }}
                      variant="contained"
                      color="inherit"
                      style={{
                        visibility: canEditInStatus() ||
                          currentApp?.appStatusId !== Constants.applicationStatuses.approved.toLowerCase() ? "hidden" : "visible", pointerEvents: 'all'
                      }}
                    > Change Bike
                    </Button>
                    <Button
                      onClick={() => {
                        changeBikeSubmit();
                      }}
                      variant="contained"
                      color="inherit"
                      style={{
                        visibility: canEditInStatus() ||
                          currentApp?.appStatusId !== Constants.applicationStatuses.approved.toLowerCase() ? "hidden" : "visible", marginLeft: "10px"
                      }}
                    > Submit
                    </Button>
                  </>
                }
              />
              {/* <Divider variant="fullWidth" /> */}
              <CardContent>
                <Grid container spacing={1}>
                  {/* VIN */}
                  <Grid item xs={6}>
                    <TextField variant="outlined" size="small"
                      label="VIN"
                      value={currentApp?.vehicle?.vin || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.vehicle) {
                            // Removes everything besides numbers and letters (technically underscores too)
                            app.vehicle.vin = e.target.value.replace(/\W/g, '').toUpperCase();
                          }
                        })
                      }}
                      fullWidth
                      error={hasValue(vinErrorMessage)}
                      helperText={hasValue(vinErrorMessage) && vinErrorMessage}
                      disabled={vinIsLocked}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    {currentApp && configuration && configuration.featureFlags?.[FeatureFlagIds.UseJDPowersport] === true &&
                      <JDPowersportButton
                        currentApp={currentApp}
                        configuration={configuration}
                        buttonText={vinIsLocked ? 'Unlock' : 'Decode'}
                        updateVinErrorMessage={updateVinErrorMessage}
                        updateVinIsLocked={updateVinIsLocked}
                        isTradeVehicle={false}
                      />
                    }
                  </Grid>
                  {/* Make */}
                  <Grid item xs={4}>
                    <TextField variant="outlined" size="small"
                      label="Make"
                      value={currentApp?.vehicle?.make || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.vehicle) {
                            app.vehicle.make = e.target.value;
                            app.vehicle.model = '';
                            app.vehicle.year = 0;
                            app.vehicle.color = '';
                            app.vehicle.mileage = 0;
                            app.vehicle.engineSize = undefined;
                            app.vehicle.nadaValue = undefined;
                          }
                        })
                      }}
                      select={!!currentApp?.availableVehicleMakes?.length}
                      SelectProps={{ native: true }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value=""></option>
                      {currentApp?.availableVehicleMakes?.map((x, key) =>
                        <option key={key} value={x}>{x}</option>
                      )}
                    </TextField>
                  </Grid>
                  {/* Year */}
                  <Grid item xs={4}>
                    <TextField variant="outlined" size="small"
                      label="Model Year"
                      value={currentApp?.vehicle?.year?.toString() || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.vehicle) {
                            app.vehicle.year = Number.parseInt(e.target.value);
                          }
                        })
                      }}
                      type="number"
                      select={!!currentApp?.availableVehicleYears?.length}
                      SelectProps={{ native: true }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value=""></option>
                      {currentApp?.availableVehicleYears?.map((x, key) =>
                        <option key={key} value={x}>{x}</option>
                      )}
                    </TextField>
                  </Grid>
                  {/* Model */}
                  <Grid item xs={4}>
                    <TextField variant="outlined" size="small"
                      label="Model"
                      value={currentApp?.vehicle?.model || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.vehicle) {
                            app.vehicle.model = e.target.value;
                          }
                        })
                      }}
                      select={!!currentApp?.availableVehicleModels?.length}
                      SelectProps={{ native: true }}
                      fullWidth
                      helperText={atcValues?.bikeType || undefined}
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value=""></option>
                      {currentApp?.availableVehicleModels?.map((x, key) =>
                        <option key={key} value={x}>{x}</option>
                      )}
                    </TextField>
                  </Grid>
                  {/* Mileage */}
                  <Grid item xs={4}>
                    <TextField variant="outlined" size="small"
                      label="Mileage"
                      value={currentApp?.vehicle?.mileage?.toString() || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.vehicle) {
                            app.vehicle.mileage = Number.parseInt(e.target.value);
                          }
                        })
                      }}
                      type="number"
                    />
                  </Grid>
                  {/* Color */}
                  <Grid item xs={4}>
                    <TextField variant="outlined" size="small"
                      label="Color"
                      value={currentApp?.vehicle?.color || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.vehicle) {
                            app.vehicle.color = e.target.value;
                          }
                        })
                      }}
                    />
                  </Grid>
                  {/* NADA Value */}
                  <Grid item xs={4}>
                    <TextField variant="outlined" size="small"
                      label="NADA Value"
                      value={currentApp?.vehicle?.nadaValue ?? 0}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.vehicle) {
                            app.vehicle.nadaValue = Number.parseFloat(Number.parseFloat(e.target.value)?.toFixed(2)) ?? undefined;
                          }
                        })
                      }}
                      type="number"
                      disabled={!atcValues.overrideJdPower || !canUnderwrite(loggedInUser?.employee)} //{currentApp?.vehicle?.make === 'Harley-Davidson'}
                      error={currentApp?.applicationQuotes?.[0]?.ruleViolations?.find(x => x.displayFieldId === 'nadavalue') !== undefined}
                      helperText={getHelperText('nadavalue', appQuote, currentApp)}
                      FormHelperTextProps={getHelperTextProps('nadavalue', appQuote)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControlLabel
                      control={<Checkbox size="small" color="primary" />}
                      label="Override"
                      labelPlacement="start"
                      disabled={!canUnderwrite(loggedInUser?.employee)}
                      checked={currentApp?.applicationCustomFields?.find(x => x.customField.name === "config_overrideJdPower")?.value === "true"}
                      onChange={(e, checked) => updateCustomValues(checked.toString(), "config_overrideJdPower")}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Placeholder"
                      value={''}
                      style={{ visibility: "hidden" }}
                      disabled
                      fullWidth
                    />
                  </Grid>
                  {/* Rough Trade Value */}
                  <Grid item xs={4}>
                    <TextField variant="outlined" size="small"
                      label="Rough Trade Value"
                      value={currentApp?.applicationCustomFields?.find(x => x.customField.name === "config_roughTrade")?.value || null}
                      onChange={(e) => updateCustomValues(Number.parseInt(e.target.value), "config_roughTrade")}
                      type="number"
                      disabled={!atcValues.overrideJdPowerRoughTrade || !canUnderwrite(loggedInUser?.employee)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControlLabel
                      control={<Checkbox size="small" color="primary" />}
                      label="Override"
                      labelPlacement="start"
                      disabled={!canUnderwrite(loggedInUser?.employee)}
                      checked={currentApp?.applicationCustomFields?.find(x => x.customField.name === "config_overrideRoughTrade")?.value === "true"}
                      onChange={(e, checked) => updateCustomValues(checked.toString(), "config_overrideRoughTrade")}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Placeholder"
                      value={''}
                      style={{ visibility: "hidden" }}
                      disabled
                      fullWidth
                    />
                  </Grid>
                  {/* Engine Size */}
                  {currentApp?.vehicle?.make !== 'Harley-Davidson' &&
                    <Grid item xs={4}>
                      <TextField variant="outlined" size="small"
                        label="Engine Size"
                        disabled={atcValues?.overrideEngineSize !== true}
                        value={currentApp?.vehicle?.engineSize ?? 0}
                        onChange={(e) => {
                          update((app) => {
                            if (app?.vehicle) {
                              app.vehicle.engineSize = Number.parseInt(e.target.value);
                            }
                          })
                        }}
                        type="number"
                      />
                    </Grid>
                  }
                  {currentApp?.vehicle?.make !== 'Harley-Davidson' && canUnderwrite(loggedInUser?.employee) && (
                    <Grid item xs={4}>
                      <FormControlLabel
                        value="top"
                        control={<Checkbox size="small" color="primary" />}
                        label="Override"
                        labelPlacement="start"
                        checked={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Override Engine Size")?.value === "true"}
                        onChange={(e, checked) => {
                          updateCustomValues(checked.toString(), "Override Engine Size");
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {/* Trade-In Vehicle Information */}
          <Grid item>
            <Card style={{ backgroundColor: cardColor, pointerEvents: canEditInStatus() ? 'all' : "none" }}>
              <CardHeader
                avatar={<InfoIcon fontSize="small" />}
                title="Trade-In 1 Vehicle"
              />
              <CardContent>
                <Grid container spacing={1}>
                  {/* VIN */}
                  <Grid item xs={6}>
                    <Label>VIN</Label>
                    <TextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle?.vin || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle) {
                            app.tradeInVehicle.vin = e.value as string;
                          } else {
                            app.tradeInVehicle = {
                              ...defaultTradeInVehicle,
                              vin: e.value as string
                            };
                          }
                        })
                      }}
                      valid={!hasValue(tradeVinErrorMessage)}
                      disabled={tradeVinIsLocked}
                    />
                    {hasValue(tradeVinErrorMessage) && <span style={{ color: '#f31700', fontSize: '12px' }}>{tradeVinErrorMessage}</span>}
                  </Grid>
                  <Grid item xs={2}>
                    {currentApp && configuration && configuration.featureFlags?.[FeatureFlagIds.UseJDPowersport] === true &&
                      <JDPowersportButton
                        currentApp={currentApp}
                        configuration={configuration}
                        buttonText={tradeVinIsLocked ? 'Unlock' : 'Decode'}
                        updateVinErrorMessage={updateTradeVinErrorMessage}
                        updateVinIsLocked={updateTradeVinIsLocked}
                        isTradeVehicle={true}
                      />
                    }
                  </Grid>
                  {/* Make */}
                  <Grid item xs={4}>
                    <Label>Make</Label>
                    <TextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle?.make || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle) {
                            app.tradeInVehicle.make = e.value as string;
                          } else {
                            app.tradeInVehicle = {
                              ...defaultTradeInVehicle,
                              make: e.value as string
                            };
                          }
                        })
                      }}
                    />
                  </Grid>
                  {/* Year */}
                  <Grid item xs={4}>
                    <Label>Model Year</Label>
                    <NumericTextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle?.year || null}
                      format='####'
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle) {
                            app.tradeInVehicle.year = e.value ?? 0;
                          } else {
                            app.tradeInVehicle = {
                              ...defaultTradeInVehicle,
                              year: e.value ?? 0
                            };
                          }
                        })
                      }}
                    />
                  </Grid>
                  {/* Model */}
                  <Grid item xs={4}>
                    <Label>Model</Label>
                    <TextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle?.model || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle) {
                            app.tradeInVehicle.model = e.value as string;
                          }
                        })
                      }}
                    />
                  </Grid>

                  {/* Mileage */}
                  <Grid item xs={4}>
                    <Label>Mileage</Label>
                    <NumericTextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle?.mileage || null}
                      format='n0'
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle) {
                            app.tradeInVehicle.mileage = e.value ?? 0;
                          } else {
                            app.tradeInVehicle = {
                              ...defaultTradeInVehicle,
                              mileage: e.value ?? 0
                            };
                          }
                        })
                      }}
                    />
                  </Grid>
                  {/* Color */}
                  <Grid item xs={4}>
                    <Label>Color</Label>
                    <TextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle?.color || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle) {
                            app.tradeInVehicle.color = e.value as string;
                          } else {
                            app.tradeInVehicle = {
                              ...defaultTradeInVehicle,
                              color: e.value as string
                            };
                          }
                        })
                      }}
                    />
                  </Grid>
                  {/* NADA Value */}
                  <Grid item xs={4}>
                    <Label>NADA Value</Label>
                    <NumericTextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle?.tradeValue ?? 0}
                      format='c2'
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle) {
                            app.tradeInVehicle.tradeValue = e.value ?? 0;
                          }
                        })
                      }}
                      disabled={!atcValues.overrideJdPower || !canUnderwrite(loggedInUser?.employee)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {/* Trade-In 2 Vehicle Information */}
          {currentApp?.tradeInVehicle2 && (
          <Grid item>
            <Card style={{ backgroundColor: cardColor, pointerEvents: canEditInStatus() ? 'all' : "none" }}>
              <CardHeader
                avatar={<InfoIcon fontSize="small" />}
                title="Trade-In 2 Vehicle"
              />
              <CardContent>
                <Grid container spacing={1}>
                  {/* VIN */}
                  <Grid item xs={6}>
                    <Label>VIN</Label>
                    <TextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle2?.vin || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle2) {
                            app.tradeInVehicle2.vin = e.value as string;
                          }
                        })
                      }}
                      valid={!hasValue(trade2VinErrorMessage)}
                      disabled={trade2VinIsLocked}
                    />
                    {hasValue(trade2VinErrorMessage) && <span style={{ color: '#f31700', fontSize: '12px' }}>{trade2VinErrorMessage}</span>}
                  </Grid>
                  <Grid item xs={2}>
                    {currentApp && configuration && configuration.featureFlags?.[FeatureFlagIds.UseJDPowersport] === true &&
                      <JDPowersportButton
                        currentApp={currentApp}
                        configuration={configuration}
                        buttonText={trade2VinIsLocked ? 'Unlock' : 'Decode'}
                        updateVinErrorMessage={updateTrade2VinErrorMessage}
                        updateVinIsLocked={updateTrade2VinIsLocked}
                        isTradeVehicle={true}
                        tradeInSlot={2}
                      />
                    }
                  </Grid>
                  {/* Make */}
                  <Grid item xs={4}>
                    <Label>Make</Label>
                    <TextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle2?.make || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle2) {
                            app.tradeInVehicle2.make = e.value as string;
                          }
                        })
                      }}
                    />
                  </Grid>
                  {/* Year */}
                  <Grid item xs={4}>
                    <Label>Model Year</Label>
                    <NumericTextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle2?.year || null}
                      format='####'
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle2) {
                            app.tradeInVehicle2.year = e.value ?? 0;
                          }
                        })
                      }}
                    />
                  </Grid>
                  {/* Model */}
                  <Grid item xs={4}>
                    <Label>Model</Label>
                    <TextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle2?.model || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle2) {
                            app.tradeInVehicle2.model = e.value as string;
                          }
                        })
                      }}
                    />
                  </Grid>

                  {/* Mileage */}
                  <Grid item xs={4}>
                    <Label>Mileage</Label>
                    <NumericTextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle2?.mileage || null}
                      format='n0'
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle2) {
                            app.tradeInVehicle2.mileage = e.value ?? 0;
                          }
                        })
                      }}
                    />
                  </Grid>
                  {/* Color */}
                  <Grid item xs={4}>
                    <Label>Color</Label>
                    <TextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle2?.color || ''}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle2) {
                            app.tradeInVehicle2.color = e.value as string;
                          }
                        })
                      }}
                    />
                  </Grid>
                  {/* NADA Value */}
                  <Grid item xs={4}>
                    <Label>NADA Value</Label>
                    <NumericTextBox
                      fillMode='outline'
                      style={{ width: '100%' }}
                      value={currentApp?.tradeInVehicle2?.tradeValue ?? 0}
                      format='c2'
                      onChange={(e) => {
                        update((app) => {
                          if (app?.tradeInVehicle2) {
                            app.tradeInVehicle2.tradeValue = e.value ?? 0;
                          }
                        })
                      }}
                      disabled={!atcValues.overrideJdPower || !canUnderwrite(loggedInUser?.employee)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          )}
          {/* Application Details */}
          <Grid item>
            <ApplicationDetailsCard
              {...props}
              salesPersonLabel="Dealer"
              preventDocumentDownload={true}
              handleSaveAppFromOverview={props.handleSaveAppFromOverview}
            />
          </Grid>
          <Grid item>
            <CardContent>
              <Grid container>
                {vehicle && vehicle.vin && vehicle.make && vehicle.model &&
                  <Grid item xs={8}>
                    <StatusWorkflowComponent />
                  </Grid>
                }
                {currentApp?.appStatusId.toUpperCase() === Constants.applicationStatuses.approved && (
                  <Grid item xs={4}>
                    <Divider variant='fullWidth' />
                    <TextField style={{ marginTop: "12px" }}
                      variant="outlined"
                      size="small"
                      label="Docs Dated"
                      value={atcValues?.docsDate}
                      onChange={(e) =>
                        updateCustomValues(e.target.value, "Docs Dated")}
                      fullWidth
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Grid>
          <Grid item>
            <Button
              color="primary"
              style={{ position: "fixed", right: "60px", top: "120px" }}
              onClick={handleCreditApplicationClick}
            >
              Credit Application
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12} lg={4} container spacing={1} direction="column">
          {/* Pricing: Front-End */}
          <Grid item>
            <Card style={{ backgroundColor: cardColor, pointerEvents: canEditInStatus() ? 'all' : "none" }}>
              <CardHeader
                avatar={<InfoIcon fontSize="small" />}
                title="Pricing: Front-End"
              />
              <Divider variant="fullWidth" />
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label={"Max Front-End Advance"}
                      value={roundNumber(atcValues?.maxFrontEndAdvance, 2)}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Remaining Available"
                      value={roundNumber(atcValues?.availableFrontend, 2)}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Bike Price"
                      value={currentApp?.applicationQuotes?.[0]?.quote?.carSalePrice || null}
                      onChange={(e) => {
                        update((app) => {
                          if (app) {
                            app.applicationQuotes[0].quote.carSalePrice = Number.parseFloat(e.target.value);
                          }
                        })
                      }}
                      style={{ pointerEvents: canEditBikeInStatus() ? 'all' : "none" }}
                      type="number"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Placeholder"
                      value={''}
                      style={{ visibility: "hidden" }}
                      disabled
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Freight / Prep"
                      value={atcValues?.freightPrep}
                      onChange={(e) => updateCustomValues(roundNumber(parseFloat(e.target.value), 2), "frontEnd_freightPrep")}
                      fullWidth
                      disabled={atcValues?.overrideFreightPrep !== true}
                      type="number"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {(
                    <Grid item xs={6}>
                      <FormControlLabel
                        value="top"
                        control={<Checkbox size="small" color="primary" />}
                        label="Override"
                        labelPlacement="start"
                        checked={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Override Freight Prep")?.value === "true"}
                        onChange={(e, checked) => {
                          updateCustomValues(checked.toString(), "Override Freight Prep");
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Dealer Doc Fee"
                      value={atcValues?.docFee}
                      onChange={(e) => updateCustomValues(roundNumber(parseFloat(e.target.value), 2), "frontEnd_docFee")}
                      fullWidth
                      disabled={atcValues?.overrideDocFee !== true}
                      type="number"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {(
                    <Grid item xs={6}>
                      <FormControlLabel
                        value="top"
                        control={<Checkbox size="small" color="primary" />}
                        label="Override"
                        labelPlacement="start"
                        checked={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Override Doc Fee")?.value === "true"}
                        onChange={(e, checked) => {
                          updateCustomValues(checked.toString(), "Override Doc Fee");
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Inspection Fee"
                      value={atcValues?.inspectionFee}
                      onChange={(e) => updateCustomValues(roundNumber(parseFloat(e.target.value), 2), "frontEnd_inspectionFee")}
                      fullWidth
                      disabled={atcValues?.overrideInspectionFee !== true}
                      type="number"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {(
                    <Grid item xs={6}>
                      <FormControlLabel
                        value="top"
                        control={<Checkbox size="small" color="primary" />}
                        label="Override"
                        labelPlacement="start"
                        checked={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Override Inspection Fee")?.value === "true"}
                        onChange={(e, checked) => {
                          updateCustomValues(checked.toString(), "Override Inspection Fee");
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Texas VIT"
                      value={roundNumber(atcValues?.vit, 2)}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Placeholder"
                      value={''}
                      style={{ visibility: "hidden" }}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="VIT Percentage"
                      value={roundNumber((atcValues?.vitPercent || 0), 6)}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Placeholder"
                      value={''}
                      style={{ visibility: "hidden" }}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Title, Registration Fees"
                      value={atcValues?.titleRegFees || null}
                      onChange={(e) => {
                        update((app) => {
                          if (app) {
                            app.applicationQuotes[0].quote.fees = Number.parseFloat(Number.parseFloat(e.target.value).toFixed(2));
                          }
                        })
                      }}
                      fullWidth
                      type="number"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Placeholder"
                      value={''}
                      style={{ visibility: "hidden" }}
                      disabled
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Total Fees"
                      value={roundNumber(atcValues?.availableTotalFees, 2)}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Placeholder"
                      value={''}
                      style={{ visibility: "hidden" }}
                      disabled
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Sales Tax"
                      value={(atcValues?.salesTax?.toFixed(2) || 0)}
                      onChange={(e) => updateCustomValues(roundNumber(parseFloat(e.target.value), 2), "frontEnd_salesTax")}
                      disabled={atcValues?.overrideSalesTax !== true}
                      fullWidth
                      error={primary?.customer?.customerAddress?.find(x => x.default === true)?.state?.abbreviation !== undefined}
                      helperText={`From State: ${primary?.customer?.customerAddress?.find(x => x.default === true)?.state?.abbreviation || null}`}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {canUnderwrite(loggedInUser?.employee) && (
                    <Grid item xs={6}>
                      <FormControlLabel
                        value="top"
                        control={<Checkbox size="small" color="primary" />}
                        label="Override"
                        labelPlacement="start"
                        checked={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Override Sales Tax")?.value === "true"}
                        onChange={(e, checked) => {
                          updateCustomValues(checked.toString(), "Override Sales Tax");
                        }}
                      />
                    </Grid>
                  )}

                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {/* Trade */}
          <Grid item>
            <Card style={{ backgroundColor: cardColor, pointerEvents: canEditInStatus() ? 'all' : "none" }}>
              <CardHeader
                avatar={<InfoIcon fontSize="small" />}
                title="Trade"
              />
              <Divider variant="fullWidth" />
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField variant="outlined" size="small"
                      label="Allowance"
                      value={currentApp?.applicationQuotes[0].quote.tradeAllowance || null}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.applicationQuotes?.[0]?.quote) {
                            app.applicationQuotes[0].quote.tradeAllowance = Number.parseFloat(e.target.value);
                          }
                        })
                      }}
                      type="number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Placeholder"
                      value={''}
                      style={{ visibility: "hidden" }}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField variant="outlined" size="small"
                      label="Lien Payoff"
                      value={currentApp?.applicationQuotes?.[0]?.quote?.tradeLien || null}
                      onChange={(e) => {
                        update((app) => {
                          if (app?.applicationQuotes?.[0]?.quote) {
                            app.applicationQuotes[0].quote.tradeLien = Number.parseFloat(e.target.value);
                          }
                        })
                      }}
                      type="number"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Placeholder"
                      value={''}
                      style={{ visibility: "hidden" }}
                      disabled
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField variant="outlined" size="small"
                      label="Net Trade Value"
                      value={atcValues?.netTradeValue || null}
                      onChange={(e) => updateCustomValues(e.target.value, "Net Trade Value")}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Checkbox size="small" color="primary" />}
                      label="Receive Tax Credit"
                      labelPlacement="start"
                      checked={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Receive Tax Credit")?.value === "true"}
                      onChange={(e, checked) => updateCustomValues(checked.toString(), "Receive Tax Credit")}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {/* Pricing: Back-End */}
          <Grid item>
            <Card style={{ backgroundColor: cardColor, pointerEvents: canEditInStatus() ? 'all' : "none" }}>
              <CardHeader
                avatar={<InfoIcon fontSize="small" />}
                title="Pricing: Back-End"
              />
              <Divider variant="fullWidth" />
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Max Back-End Advance"
                      value={atcValues?.maxBackendAdvance}
                      onChange={(e) => updateCustomValues(Number.parseFloat(e.target.value), "backEnd_maxBackEndAdvance")}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Remaining Available"
                      value={Math.max(0, atcValues?.availableBackend || 0)}
                      onChange={(e) => updateCustomValues(Number.parseFloat(e.target.value), "backEnd_available")}
                      InputProps={{
                        readOnly: true,
                      }}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={9}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="GAP"
                      value={atcValues?.gap || null}
                      type="number"
                      onChange={(e) => updateCustomValues(Number.parseFloat(e.target.value), "GAP")}
                      fullWidth
                      error={currentApp?.applicationQuotes?.[0]?.ruleViolations?.find(x => x.displayFieldId === 'gapvalue') !== undefined}
                      helperText={`Max: ${atcValues?.maxGap || null}`}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Term"
                      value={currentApp?.applicationQuotes[0].quote.term}
                      //onChange={(e) => updateCustomValues(Number.parseInt(e.target.value), "GAP Term")}
                      style={{ visibility: (atcValues?.gap ?? 0) > 0 ? "visible" : "hidden" }}
                      fullWidth
                      disabled
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={9}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Service Contract"
                      type="number"
                      value={atcValues?.serviceContract || null}
                      onChange={(e) => updateCustomValues(Number.parseFloat(e.target.value), "Service Contract")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Term"
                      value={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Service Contract Term")?.value || null}
                      onChange={(e) => updateCustomValues(Number.parseInt(e.target.value), "Service Contract Term")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={9}>
                    <TextField
                      variant="outlined"
                      size="small"
                      type="number"
                      label="Tire and Wheel"
                      value={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Tire and Wheel")?.value || null}
                      onChange={(e) => updateCustomValues(e.target.value, "Tire and Wheel")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Term"
                      value={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Tire and Wheel Term")?.value}
                      onChange={(e) => updateCustomValues(Number.parseFloat(e.target.value), "Tire and Wheel Term")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {atcValues && atcValues.availableBackend !== undefined && atcValues.availableBackend < 0 && (
                    <Grid item xs={12}>
                      <span style={{ color: 'red', alignSelf: 'center', fontSize: '14px' }}>
                        TOO MUCH BACK-END ASSIGNED
                      </span>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item>
            <InvalidStipulationsCard
              appCustomerStipDocRequirements={props.appCustomerStipDocRequirements}
              appCustomers={props.appCustomers}
              navigate={props.navigate}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} lg={4} container spacing={1} direction="column">
          {/* Funding Basis */}
          <Grid item>
            <Card style={{ backgroundColor: cardColor, pointerEvents: canEditInStatus() ? 'all' : "none" }}>
              <CardHeader
                avatar={<InfoIcon fontSize="small" />}
                title="Funding Basis"
              />
              <Divider variant="fullWidth" />
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField variant="outlined" size="small"
                      label="Transation Type"
                      value={currentApp?.applicationCustomFields.find(x => x.customField.name === "Loan Type")?.value || 'Lease'}
                      onChange={(e) => updateCustomValues(e.target.value, "Loan Type")}
                      select={!!currentApp?.availableTransactionTypes?.length}
                      SelectProps={{ native: true }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value=""></option>
                      {currentApp?.availableTransactionTypes?.map((x, key) =>
                        <option key={key} value={x}>{x}</option>
                      )}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      variant="outlined"
                      label="Term (in months)"
                      type="number"
                      fullWidth
                      value={currentApp?.applicationQuotes[0].quote.term}
                      onChange={(e) => {
                        update((app) => {
                          if (app) {
                            app.applicationQuotes[0].quote.term = Number.parseInt(e.target.value as string);
                          }
                        });
                      }}
                      select={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Override Payment Term")?.value !== "true"}
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value=""></option>
                      {atcValues?.terms?.map((v, key) => (
                        <option key={key} value={v}>{v}</option>
                      ))}
                    </TextField>
                  </Grid>
                  {canUnderwrite(loggedInUser?.employee) && (
                    <Grid item xs={6}>
                      <FormControlLabel
                        value="top"
                        control={<Checkbox size="small" color="primary" />}
                        label="Override"
                        labelPlacement="start"
                        checked={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Override Payment Term")?.value === "true"}
                        onChange={(e, checked) => {
                          // If user unchecks, set payment term to 0
                          if (!checked) {
                            update((app) => {
                              if (app) {
                                app.applicationQuotes[0].quote.term = 0;
                              }
                            });
                          }
                          updateCustomValues(checked.toString(), "Override Payment Term");
                        }}
                      />
                    </Grid>
                  )}
                  {!canUnderwrite(loggedInUser?.employee) && (atcValues?.loanType !== "Lease") && (
                    <Grid item xs={6}>
                      <TextField
                        variant="outlined"
                        size="small"
                        label="APR %"
                        value={((atcValues?.apr || 0) * 100).toFixed(2)}
                        fullWidth
                        disabled
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  )}
                  {!canUnderwrite(loggedInUser?.employee) && (atcValues?.loanType === "Lease") && (
                    <Grid item xs={6}>
                      <TextField
                        variant="outlined"
                        size="small"
                        label="Placeholder"
                        value={''}
                        style={{ visibility: "hidden" }}
                        disabled
                        fullWidth
                      />
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Cash Paid at Signing"
                      value={cashPaidAtSigning || 0}
                      onChange={(e) => {
                        update((app) => {
                          if (app) {
                            app.applicationQuotes[0].quote.cashDown = Number.parseFloat(e.target.value);
                          }
                        });
                      }}
                      fullWidth
                      error={atcValues.cashPaidAtSigningError && showAdjustedCashDown === false}
                      helperText={`Min req: ${atcValues?.minCashPaidAtSigning}`}
                      type="number"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Placeholder"
                      value={''}
                      style={{ visibility: "hidden" }}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label={`${atcValues?.loanType} payment`}
                      value={roundNumber(atcValues?.monthlyPayment || 0, 2)}
                      disabled
                      fullWidth
                      error={atcValues?.paymentError}
                      helperText={primary?.customer?.customerAddress?.find(x => x.default === true)?.state?.abbreviation === 'NC'
                        ? `Max Payment Including Tax $${atcValues?.maxPaymentAfterTax?.toFixed(2) || 0}`
                        : `Max Allowed: ${atcValues?.maxPayment?.toFixed(2) || 0}`}
                      InputLabelProps={{ shrink: true }}
                    // FormHelperTextProps={paymentError ? undefined : { style: { visibility: 'hidden' } as CSSProperties }}
                    />
                  </Grid>
                  {showMonthlyPaymentTax() && atcValues?.loanType === "Lease" && (
                    <>
                      <Grid item xs={6}>
                        <TextField
                          variant="outlined"
                          size="small"
                          label="Placeholder"
                          value={''}
                          style={{ visibility: "hidden" }}
                          disabled
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          variant="outlined"
                          size="small"
                          label={`${atcValues?.loanType} payment tax`}
                          value={atcValues?.monthlyPaymentSalesTax?.toFixed(2)}
                          disabled
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        //error={atcValues?.paymentError}
                        //helperText={`Max Allowed: ${atcValues?.maxPayment || 0}`}
                        // FormHelperTextProps={paymentError ? undefined : { style: { visibility: 'hidden' } as CSSProperties }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          variant="outlined"
                          size="small"
                          label="Placeholder"
                          value={''}
                          style={{ visibility: "hidden" }}
                          disabled
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          variant="outlined"
                          size="small"
                          label={`${atcValues?.loanType} payment total with tax`}
                          value={atcValues?.monthlyTotalPaymentWithTax}
                          disabled
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        //error={atcValues?.paymentError}
                        //helperText={`Max Allowed: ${atcValues?.maxPayment || 0}`}
                        // FormHelperTextProps={paymentError ? undefined : { style: { visibility: 'hidden' } as CSSProperties }}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Placeholder"
                      value={''}
                      style={{ visibility: "hidden" }}
                      disabled
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label={atcValues?.loanType === "Lease" ? "Residual Value" : "Finance Discount"}
                      value={atcValues?.loanType === "Lease" ? roundNumber(atcValues?.residual, 2) : currentApp?.applicationQuotes[0].quote.discount || 0}
                      onChange={(e) => {
                        if (atcValues?.loanType !== "Lease") {
                          update((app) => {
                            if (app) {
                              app.applicationQuotes[0].quote.discount = Number.parseFloat(e.target.value);
                            }
                          })
                        }
                      }}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Placeholder"
                      value={''}
                      style={{ visibility: "hidden" }}
                      disabled
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label={"Total Sales Amount"}
                      value={atcValues?.fbTotalSaleAmount}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {
                    atcValues?.tierPrimary && (atcValues.tierPrimary >= 5 && atcValues.tierPrimary <= 8) && (
                      <Grid item xs={6} style={{ display: 'flex' }}>
                        <span style={{ color: 'red', alignSelf: 'center', fontSize: '14px' }}>
                          GPS REQUIRED
                        </span>
                      </Grid>
                    )
                  }
                  {
                    atcValues?.tierPrimary && (atcValues.tierPrimary >= 1 && atcValues.tierPrimary <= 4) && (
                      <Grid item xs={6} style={{ display: 'flex' }}>
                        <span style={{ color: 'green', alignSelf: 'center', fontSize: '14px' }}>
                          GPS NOT REQUIRED
                        </span>
                      </Grid>
                    )
                  }
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label={"Net Paid to Dealer"}
                      value={roundNumber(atcValues?.netPaidToDealer, 2)}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {
                    maxAmountToFinanceRuleViolation && (
                      <Grid item xs={6} style={{ display: 'flex' }}>
                        <span style={{ color: 'red', alignSelf: 'center', fontSize: '14px' }}>
                          {
                            maxAmountToFinanceRuleViolation.summary
                          }
                        </span>
                      </Grid>
                    )
                  }
                  {
                    !maxAmountToFinanceRuleViolation && displayLtvCashDue && (
                      <Grid item xs={6} style={{ display: 'flex' }}>
                        <span style={{ color: 'red', alignSelf: 'center', fontSize: '14px' }}>
                          LTV ADJUSTED CASH DUE: ${roundNumber(
                            adjustedCashDue
                            , 2
                          )}
                        </span>
                      </Grid>
                    )
                  }
                  {
                    minLoanAmountNotMet && atcValues?.loanType !== "Lease" && (
                      <Grid item xs={6} style={{ display: 'flex' }}>
                        <span style={{ color: 'red', alignSelf: 'center', fontSize: '14px' }}>
                          MINIMUM LOAN AMOUNT OF $5000 NOT MET
                        </span>
                      </Grid>
                    )
                  }
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {/*Deal Summary*/}
          {canUnderwrite(loggedInUser?.employee) && (
            <>
              {isLease ?
                <Grid item>
                  <Card style={{ backgroundColor: cardColor }}>
                    <CardHeader
                      avatar={<InfoIcon fontSize="small" />}
                      title="Lease Deal Summary"
                    />
                    <Divider variant="fullWidth" />
                    <CardContent>
                      <Grid container>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Selling Price"
                              value={roundNumber(atcValues?.adminSellingPrice, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Cash Down"
                              value={roundNumber(atcValues?.adminCashDown, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="APR %"
                              value={((atcValues?.apr || 0) * 100).toFixed(2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Security Deposit"
                              value={atcValues?.securityDeposit}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Residual Percent"
                              value={roundNumber((atcValues?.residualPercent || 0) * 100, 4)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                              error={atcValues?.residualPercentError}
                              helperText={`Residual Percent is above max allowed`}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="GAP Fee"
                              value={atcValues?.gap}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Number of Payments"
                              value={currentApp?.applicationQuotes[0].quote.term}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Doc Fee"
                              value={atcValues?.docFee}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Trade Allowance"
                              value={currentApp?.applicationQuotes?.[0]?.quote?.tradeAllowance || null}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              disabled
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Title Fee"
                              value={roundNumber(atcValues?.titleFee, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Trade Lien"
                              value={currentApp?.applicationQuotes?.[0]?.quote?.tradeLien || null}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              disabled
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Service Warranty"
                              value={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Service Contract")?.value || null}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              disabled
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Divider variant="fullWidth" style={{ margin: "25px 0px" }} />
                      <Grid container>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label={`${atcValues?.dealTermMonths} Payments @`}
                              value={roundNumber(atcValues?.paymentAtTerm, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Disposition Fee"
                              value={roundNumber(atcValues?.dispositionFee, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Selling Price"
                              value={roundNumber(atcValues?.adminSellingPrice, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Acquisition Fee"
                              value={roundNumber(atcValues?.acquisitionFee, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Total of Payments"
                              value={roundNumber(atcValues?.totalOfPayments, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            /></Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Sales Tax"
                              value={(atcValues?.salesTax?.toFixed(2) || 0)}
                              fullWidth
                              disabled
                              error={primary?.customer?.customerAddress?.find(x => x.default === true)?.state?.abbreviation !== undefined}
                              helperText={`From State: ${primary?.customer?.customerAddress?.find(x => x.default === true)?.state?.abbreviation || null}`}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Gross Cap Cost"
                              value={roundNumber(atcValues?.grossCapCost, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            /> </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Service Fee"
                              value={roundNumber(atcValues?.serviceFee, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Total Depreciation"
                              value={roundNumber(atcValues?.totalDepreciation, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            /> </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Tire Wheel"
                              value={roundNumber(atcValues?.tireAndWheel, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Residual Value"
                              value={roundNumber(atcValues?.residual, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}></Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Total Down"
                              value={roundNumber(atcValues?.cashDown, 2)}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              disabled
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                :
                <Grid item>
                  <Card style={{ backgroundColor: cardColor }}>
                    <CardHeader
                      avatar={<InfoIcon fontSize="small" />}
                      title="Finance Deal Summary"
                    />
                    <Divider variant="fullWidth" />
                    <CardContent>
                      <Grid container>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Interest Rate"
                              value={((atcValues?.apr || 0) * 100).toFixed(2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="GAP Fee"
                              value={roundNumber(atcValues?.gap, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Number of Payments"
                              value={currentApp?.applicationQuotes[0].quote.term}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Doc Fee"
                              value={roundNumber(atcValues?.docFee, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Selling Price"
                              value={roundNumber(atcValues?.adminSellingPrice, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Title Fee"
                              value={roundNumber(atcValues?.titleFee, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Trade Allowance"
                              value={roundNumber(currentApp?.applicationQuotes?.[0]?.quote?.tradeAllowance || 0, 2)}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              disabled
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Service Warranty"
                              value={currentApp?.applicationCustomFields?.find(x => x.customField.name === "Service Contract")?.value || null}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              disabled
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Trade Lien"
                              value={roundNumber(currentApp?.applicationQuotes?.[0]?.quote?.tradeLien || 0, 2)}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              disabled
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Tire Wheel"
                              value={roundNumber(atcValues?.tireAndWheel, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Total Down"
                              value={roundNumber(atcValues?.cashDown, 2)}
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              disabled
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Inv Tax"
                              value={roundNumber(atcValues?.vit, 2)}
                              InputLabelProps={{ shrink: true }}
                              disabled
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6}></Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Inspection Fee"
                              value={roundNumber(atcValues?.inspectionFee, 2)}
                              disabled
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Divider variant="fullWidth" style={{ margin: "25px 0px" }} />
                      <Grid container>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label={`${atcValues?.dealTermMonths} Payments @`}
                              value={roundNumber(atcValues?.paymentAtTerm, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}></Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Selling Price"
                              value={roundNumber(0, 2)} // NOTE: This value is always Zero. I don't understand why they even want it to display in this case, but they apparently do.
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}></Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Finance Charge"
                              value={roundNumber(atcValues?.financeCharge, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}></Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Amount Financed"
                              value={roundNumber(atcValues?.amountFinanced, 2)}
                              helperText={`( ${atcValues?.nptdPercentageOfNada} % of NADA value)`}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}></Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Total of Payments"
                              value={roundNumber(atcValues?.totalOfPayments, 2)}
                              fullWidth
                              disabled
                              InputLabelProps={{ shrink: true }}
                            /></Grid>
                          <Grid item xs={6}></Grid>
                          <Grid item xs={6}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label="Sales Tax"
                              value={(atcValues?.salesTax?.toFixed(2) || 0)}
                              fullWidth
                              disabled
                              error={primary?.customer?.customerAddress?.find(x => x.default === true)?.state?.abbreviation !== undefined}
                              helperText={`From State: ${primary?.customer?.customerAddress?.find(x => x.default === true)?.state?.abbreviation || null}`}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}></Grid>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              }
            </>
          )}
        </Grid>
      </Grid>
    </div>
  );
}
